import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import {
  FaChevronDown,
  FaChevronRight,
  FaImage,
  FaList,
  FaParagraph,
  FaSearch,
  FaTimes,
  FaVideo,
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { useEditMode } from '../../context/EditModeContext';
import {
  getFieldPreview,
  getFieldShortLabel,
  getFieldType,
  getPageEditorMeta,
  groupFieldsBySection,
  shouldSkipEditorPanelField,
} from '../../data/editorFieldHelpers';
import { getEditorPanelDetail, hasEditorPanelDetail } from './editorPanelFieldDetails';
import './editorPanel.css';

const TYPE_LABELS = {
  text: 'Text',
  image: 'Image',
  background: 'Background',
  gallery: 'Gallery',
  videos: 'Videos',
  content: 'Content',
};

function scanEditableFields() {
  const nodes = document.querySelectorAll('main [data-content-key]');
  const byKey = new Map();

  nodes.forEach((element) => {
    if (element.closest('.editor-panel, .edit-mode-panel-tab')) return;

    const key = element.getAttribute('data-content-key');
    if (!key) return;
    if (shouldSkipEditorPanelField(key)) return;

    const current = byKey.get(key);
    const score = element.classList.contains('editable-text') ? 2 : element.classList.contains('editor-field-anchor') ? 1 : 0;
    const currentScore = current?.score ?? -1;

    if (!current || score > currentScore) {
      byKey.set(key, {
        key,
        preview: getFieldPreview(element),
        type: getFieldType(element),
        score,
      });
    }
  });

  return Array.from(byKey.values()).map(({ score, ...field }) => field);
}

function FieldTypeIcon({ type }) {
  if (type === 'videos') return <FaVideo aria-hidden="true" />;
  if (type === 'image' || type === 'background' || type === 'gallery') return <FaImage aria-hidden="true" />;
  return <FaParagraph aria-hidden="true" />;
}

function matchesSearch(field, query) {
  if (!query) return true;
  const haystack = `${field.label || ''} ${field.preview || ''} ${field.key || ''}`.toLowerCase();
  return haystack.includes(query);
}

export default function EditorPanel() {
  const { isEditing } = useAuth();
  const { panelOpen, setPanelOpen, focusField, activeFieldKey } = useEditMode();
  const location = useLocation();
  const [fields, setFields] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedSections, setCollapsedSections] = useState({});

  const pageMeta = getPageEditorMeta(location.pathname);

  const refreshFields = useCallback(() => {
    setFields(scanEditableFields());
  }, []);

  useEffect(() => {
    if (!isEditing || !panelOpen) return undefined;

    refreshFields();
    const timer = window.setTimeout(refreshFields, 400);

    const observer = new MutationObserver(() => {
      window.requestAnimationFrame(refreshFields);
    });

    const pageRoot = document.querySelector('main');
    if (!pageRoot) return undefined;

    observer.observe(pageRoot, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [isEditing, panelOpen, location.pathname, refreshFields]);

  useEffect(() => {
    setSearchQuery('');
    setCollapsedSections({});
  }, [location.pathname]);

  const groupedFields = useMemo(
    () => groupFieldsBySection(fields, location.pathname),
    [fields, location.pathname]
  );

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredGroups = useMemo(() => {
    if (!normalizedQuery) return groupedFields;

    return groupedFields
      .map((group) => ({
        ...group,
        items: group.items.filter((field) => matchesSearch(field, normalizedQuery)),
      }))
      .filter((group) => group.items.length > 0);
  }, [groupedFields, normalizedQuery]);

  const totalFieldCount = fields.length;

  const toggleSection = (sectionName) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionName]: !prev[sectionName],
    }));
  };

  if (!isEditing) return null;

  if (!panelOpen) {
    return createPortal(
      <button
        type="button"
        className="edit-mode-panel-tab"
        onClick={() => setPanelOpen(true)}
        aria-label="Open content editor panel"
      >
        <span className="edit-mode-panel-tab__icon" aria-hidden="true">
          <FaList />
        </span>
        <span className="edit-mode-panel-tab__label">Editor</span>
      </button>,
      document.body
    );
  }

  return createPortal(
    <aside className="editor-panel" aria-label="Page content editor">
      <div className="editor-panel__header">
        <div className="editor-panel__header-top">
          <div className="editor-panel__header-copy">
            <p className="editor-panel__eyebrow">Content editor</p>
            <h2 className="editor-panel__title">{pageMeta?.title || 'This page'}</h2>
          </div>
          <button
            type="button"
            className="editor-panel__close"
            onClick={() => setPanelOpen(false)}
            aria-label="Close editor panel"
          >
            <FaTimes />
          </button>
        </div>

        <p className="editor-panel__subtitle">
          {totalFieldCount} editable {totalFieldCount === 1 ? 'field' : 'fields'} on this page
        </p>

        <div className="editor-panel__search">
          <FaSearch className="editor-panel__search-icon" aria-hidden="true" />
          <input
            type="search"
            className="editor-panel__search-input"
            placeholder="Search fields…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search editable fields"
          />
          {searchQuery && (
            <button
              type="button"
              className="editor-panel__search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              <FaTimes />
            </button>
          )}
        </div>
      </div>

      <div className="editor-panel__body">
        {filteredGroups.length === 0 ? (
          <div className="editor-panel__empty">
            {normalizedQuery ? (
              <>
                <p>No fields match your search.</p>
                <p className="editor-panel__empty-hint">Try a different keyword or clear the search.</p>
              </>
            ) : (
              <>
                <p>No editable fields found on this page yet.</p>
                <p className="editor-panel__empty-hint">Navigate to Home, Story, Ministries, or another content page.</p>
              </>
            )}
          </div>
        ) : (
          filteredGroups.map(({ section, items }) => {
            const isCollapsed = Boolean(collapsedSections[section]);

            return (
              <section key={section} className="editor-panel__section">
                <button
                  type="button"
                  className="editor-panel__section-toggle"
                  onClick={() => toggleSection(section)}
                  aria-expanded={!isCollapsed}
                >
                  <span className="editor-panel__section-title">{section}</span>
                  <span className="editor-panel__section-meta">
                    <span className="editor-panel__section-count">{items.length}</span>
                    <FaChevronDown
                      className={`editor-panel__section-chevron${isCollapsed ? '' : ' is-open'}`}
                      aria-hidden="true"
                    />
                  </span>
                </button>

                {!isCollapsed && (
                  <ul className="editor-panel__list">
                    {items.map((field) => {
                      const DetailComponent = getEditorPanelDetail(field.key);
                      const isActive = activeFieldKey === field.key;
                      const hasDetail = hasEditorPanelDetail(field.key);
                      const fieldLabel = getFieldShortLabel(field.key);
                      const typeLabel = TYPE_LABELS[field.type] || 'Content';

                      return (
                        <li
                          key={field.key}
                          className={hasDetail && isActive ? 'editor-panel__list-item--expanded' : ''}
                        >
                          <button
                            type="button"
                            className={`editor-panel__item${isActive ? ' is-active' : ''}${hasDetail ? ' editor-panel__item--expandable' : ''}`}
                            onClick={() => focusField(field.key)}
                            aria-expanded={hasDetail ? isActive : undefined}
                          >
                            <span className={`editor-panel__item-icon editor-panel__item-icon--${field.type}`}>
                              <FieldTypeIcon type={field.type} />
                            </span>
                            <span className="editor-panel__item-copy">
                              <span className="editor-panel__item-label-row">
                                <span className="editor-panel__item-label">{fieldLabel}</span>
                                <span className="editor-panel__item-type">{typeLabel}</span>
                              </span>
                              <span className="editor-panel__item-preview">{field.preview}</span>
                            </span>
                            {hasDetail ? (
                              <FaChevronDown
                                className={`editor-panel__item-chevron${isActive ? ' is-open' : ''}`}
                                aria-hidden="true"
                              />
                            ) : (
                              <FaChevronRight className="editor-panel__item-arrow" aria-hidden="true" />
                            )}
                          </button>
                          {hasDetail && isActive && DetailComponent && (
                            <DetailComponent contentKey={field.key} />
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            );
          })
        )}
      </div>

      <div className="editor-panel__footer">
        <p className="editor-panel__footer-title">Auto-save enabled</p>
        <p className="editor-panel__footer-copy">
          Click a field to jump to it on the page. Expand grouped items to edit details here.
        </p>
      </div>
    </aside>,
    document.body
  );
}
